import { useState, useRef, useEffect } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  BookOpen,
  FolderOpen,
  PlayCircle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Link2,
} from 'lucide-react'
import { adminApi, curriculumBuilderApi, type AdminContent } from '@/api/admin'
import { CreateContentModal } from './CreateContentModal'
import { ContentMetadataForm } from './ContentMetadataForm'
import { ModuleLessonEditor } from './ModuleLessonEditor'

const TREE_PANEL_WIDTH_STORAGE_KEY = 'curriculum-builder-tree-panel-width'
const DEFAULT_TREE_PANEL_WIDTH = 320
const MIN_TREE_PANEL_WIDTH = 240
const MAX_TREE_PANEL_WIDTH = 640
const MIN_DETAIL_PANEL_WIDTH = 480

const LEVEL_ICONS: Record<string, React.ElementType> = {
  curriculum: BookOpen,
  course: FolderOpen,
  module: FolderOpen,
  lesson: PlayCircle,
}

const LEVEL_COLORS: Record<string, string> = {
  curriculum: 'text-blue-600 bg-blue-50',
  course: 'text-purple-600 bg-purple-50',
  module: 'text-amber-600 bg-amber-50',
  lesson: 'text-green-600 bg-green-50',
}

const LEVEL_LABELS: Record<string, string> = {
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
  lesson: '레슨',
}

/** Allowed child types for each parent type */
const ALLOWED_CHILDREN: Record<string, string[]> = {
  curriculum: ['course', 'module', 'lesson'],
  course: ['module', 'lesson'],
  module: ['lesson'],
  lesson: [],
}

interface CurriculumEditorProps {
  curriculumId: number
}

export function CurriculumEditor({ curriculumId }: CurriculumEditorProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const contentRef = useRef<HTMLDivElement>(null)
  const [treePanelWidth, setTreePanelWidth] = useState(() => {
    const savedWidth = window.localStorage.getItem(TREE_PANEL_WIDTH_STORAGE_KEY)
    const parsedWidth = savedWidth ? Number(savedWidth) : DEFAULT_TREE_PANEL_WIDTH
    return clampTreePanelWidth(Number.isFinite(parsedWidth) ? parsedWidth : DEFAULT_TREE_PANEL_WIDTH)
  })
  const [selectedId, setSelectedId] = useState<number | null>(curriculumId)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set([curriculumId]))
  const [editingLessonsForId, setEditingLessonsForId] = useState<number | null>(null)
  const [createModal, setCreateModal] = useState<{ level: 'curriculum' | 'course' | 'module'; parentId: number; parentTitle: string } | null>(null)

  // Fetch curriculum with hierarchy
  const { data: hierarchy, isLoading, error } = useQuery({
    queryKey: ['curriculum-builder', 'hierarchy', curriculumId],
    queryFn: () => adminApi.getContentHierarchy(curriculumId, true),
  })

  const selectedContent = selectedId ? findInHierarchy(hierarchy, selectedId) : null
  const editingLessonsContent = editingLessonsForId ? findInHierarchy(hierarchy, editingLessonsForId) : null

  useEffect(() => {
    window.localStorage.setItem(TREE_PANEL_WIDTH_STORAGE_KEY, String(treePanelWidth))
  }, [treePanelWidth])

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleNodeSelect = (id: number) => {
    setSelectedId(id)
  }

  const handleContentUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
  }

  const handleCreated = (content: AdminContent) => {
    setCreateModal(null)
    queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
    setExpandedIds((prev) => new Set([...prev, content.parentId!]))
    setSelectedId(content.id)
  }

  const handleMoveChild = (parentId: number, childId: number, direction: -1 | 1) => {
    const queryKey = ['curriculum-builder', 'hierarchy', curriculumId]
    const prev = queryClient.getQueryData<any>(queryKey)
    if (!prev) return

    const parent = findInHierarchy(prev, parentId)
    if (!parent?.children) return

    const index = parent.children.findIndex((c: any) => c.id === childId)
    if (index === -1) return
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= parent.children.length) return

    // Reorder siblings (adjacent swap via splice)
    const newChildren = [...parent.children]
    const [moved] = newChildren.splice(index, 1)
    newChildren.splice(newIndex, 0, moved)

    // Optimistic cache update
    queryClient.setQueryData(queryKey, replaceChildrenInTree(prev, parentId, newChildren))

    // Persist full sibling order (consistent with lesson reorder)
    const orders = newChildren.map((c: any, i: number) => ({ contentId: c.id, sequenceOrder: i + 1 }))
    curriculumBuilderApi
      .reorderChildren(parentId, orders)
      .then(() => {
        queryClient.invalidateQueries({ queryKey })
      })
      .catch(() => {
        queryClient.setQueryData(queryKey, prev) // rollback
    })
  }

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()

    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const bounds = contentRef.current?.getBoundingClientRect()
      if (!bounds) return

      const maxWidth = Math.max(
        MIN_TREE_PANEL_WIDTH,
        Math.min(MAX_TREE_PANEL_WIDTH, bounds.width - MIN_DETAIL_PANEL_WIDTH)
      )
      const nextWidth = moveEvent.clientX - bounds.left
      setTreePanelWidth(clampTreePanelWidth(nextWidth, maxWidth))
    }

    const handlePointerUp = () => {
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
  }

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const direction = event.key === 'ArrowLeft' ? -1 : 1
    const step = event.shiftKey ? 40 : 16
    setTreePanelWidth((width) => clampTreePanelWidth(width + direction * step))
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error || !hierarchy) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <span>커리큘럼을 불러올 수 없습니다.</span>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-slate-200 flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate('/contents/curriculum-builder')}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-slate-900"><ContentMetadataText text={hierarchy.title} /></h1>
          <p className="text-sm text-slate-500">{LEVEL_LABELS[hierarchy.type]} 편집</p>
        </div>
      </div>

      {/* Content */}
      <div ref={contentRef} className="flex-1 flex overflow-hidden">
        {editingLessonsContent ? (
          /* Lesson editor mode: full-width ModuleLessonEditor (tree hidden) */
          <ModuleLessonEditor
            key={editingLessonsForId}
            parentId={editingLessonsContent.id}
            parentTitle={editingLessonsContent.title}
            parentType={editingLessonsContent.type}
            lessons={(editingLessonsContent.children || []).filter((c: any) => c.type === 'lesson').map((c: any) => ({
              id: c.id,
              title: c.title,
              qid: c.qid,
            }))}
            curriculumId={curriculumId}
            onUpdated={handleContentUpdated}
            onBack={() => {
              setEditingLessonsForId(null)
            }}
          />
        ) : (
          /* Normal 2-pane: Tree + Detail */
          <>
            {/* Left: Tree */}
            <div
              className="bg-white overflow-y-auto shrink-0"
              style={{ width: `${treePanelWidth}px` }}
            >
              <div className="p-3">
                <TreeNodeView
                  node={hierarchy}
                  level={0}
                  index={0}
                  siblingCount={1}
                  selectedId={selectedId}
                  expandedIds={expandedIds}
                  onSelect={handleNodeSelect}
                  onToggle={toggleExpand}
                  onMoveChild={handleMoveChild}
                  onAddChild={(parentId, parentTitle, childLevel) => {
                    if (childLevel === 'lesson') {
                      setSelectedId(parentId)
                      setExpandedIds((prev) => new Set([...prev, parentId]))
                      setEditingLessonsForId(parentId)
                    } else {
                      setCreateModal({ level: childLevel as 'curriculum' | 'course' | 'module', parentId, parentTitle })
                    }
                  }}
                  onRemoveChild={(parentId, childId) => {
                    const child = findInHierarchy(hierarchy, childId)
                    const attachCount = child?.attachedParentsCount ?? 1
                    const msg = attachCount >= 2
                      ? `이 항목은 ${attachCount}개의 부모에 attach되어 있습니다. 이 부모에서만 제거됩니다. 진행할까요?`
                      : '이 항목을 제거하시겠습니까? 다른 부모에 attach되어 있지 않아 어디에도 속하지 않게 됩니다.'
                    if (confirm(msg)) {
                      curriculumBuilderApi.removeChildFromParent(parentId, childId).then(() => {
                        queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
                        if (selectedId === childId) setSelectedId(parentId)
                      })
                    }
                  }}
                />
              </div>
            </div>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="트리 패널 너비 조절"
              aria-valuemin={MIN_TREE_PANEL_WIDTH}
              aria-valuemax={MAX_TREE_PANEL_WIDTH}
              aria-valuenow={treePanelWidth}
              tabIndex={0}
              onPointerDown={handleResizeStart}
              onKeyDown={handleResizeKeyDown}
              className="group relative w-2 shrink-0 cursor-col-resize border-l border-r border-slate-200 bg-slate-50 outline-none transition-colors hover:bg-blue-50 focus:bg-blue-50"
            >
              <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-transparent transition-colors group-hover:bg-blue-300 group-focus:bg-blue-400" />
            </div>

            {/* Right: Detail form */}
            <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
              {selectedContent ? (
                <ContentMetadataForm
                  key={selectedId}
                  content={selectedContent}
                  onUpdated={handleContentUpdated}
                  onEditLessons={
                    selectedContent.type === 'module'
                      ? () => setEditingLessonsForId(selectedContent.id)
                      : undefined
                  }
                />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  좌측 트리에서 항목을 선택하세요
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {createModal && (
        <CreateContentModal
          level={createModal.level}
          parentId={createModal.parentId}
          parentTitle={createModal.parentTitle}
          existingChildIds={
            new Set(
              (findInHierarchy(hierarchy, createModal.parentId)?.children ?? [])
                .map((c: any) => c.id)
            )
          }
          onClose={() => setCreateModal(null)}
          onCreated={handleCreated}
          onAttached={(contents) => {
            queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
            setExpandedIds((prev) => new Set([...prev, createModal.parentId]))
            if (contents.length > 0) {
              setSelectedId(contents[0].id)
            }
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Tree Node Component
// ============================================================================

interface TreeNodeViewProps {
  node: any
  level: number
  index: number
  siblingCount: number
  selectedId: number | null
  expandedIds: Set<number>
  onSelect: (id: number) => void
  onToggle: (id: number) => void
  onAddChild: (parentId: number, parentTitle: string, childLevel: string) => void
  onRemoveChild: (parentId: number, childId: number) => void
  onMoveChild: (parentId: number, childId: number, direction: -1 | 1) => void
}

function TreeNodeView({ node, level, index, siblingCount, selectedId, expandedIds, onSelect, onToggle, onAddChild, onRemoveChild, onMoveChild }: TreeNodeViewProps) {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const isSelected = selectedId === node.id
  const Icon = LEVEL_ICONS[node.type] || FolderOpen
  const colorClass = LEVEL_COLORS[node.type] || 'text-slate-600 bg-slate-50'

  const allowedChildren = ALLOWED_CHILDREN[node.type] || []
  const canAddChild = allowedChildren.length > 0

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  return (
    <div>
      <div
        className={`group flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
          isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50 text-slate-700'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {/* Expand toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(node.id) }}
          className={`w-5 h-5 flex items-center justify-center shrink-0 ${hasChildren ? '' : 'invisible'}`}
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>

        {/* Icon + Label */}
        <div className="flex items-center gap-2 flex-1 min-w-0" onClick={() => onSelect(node.id)}>
          <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${colorClass}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="text-sm truncate"><ContentMetadataText text={node.title} /></span>
        </div>

        {(node.attachedParentsCount ?? 0) >= 2 && (
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 rounded shrink-0"
            title={`${node.attachedParentsCount}개 부모에서 공유 중`}
          >
            <Link2 className="w-3 h-3" />
            {node.attachedParentsCount}
          </span>
        )}

        {/* Reorder: course/module only */}
        {(node.type === 'course' || node.type === 'module') && (
          <div className="flex items-center shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); onMoveChild(0, node.id, -1) }}
              disabled={index === 0}
              className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title="위로 이동"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onMoveChild(0, node.id, 1) }}
              disabled={index === siblingCount - 1}
              className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title="아래로 이동"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Add child: dropdown menu if multiple options, direct action if single */}
        {canAddChild && (
          <div className="relative" ref={menuRef}>
            {allowedChildren.length === 1 ? (
              <button
                onClick={(e) => { e.stopPropagation(); onAddChild(node.id, node.title, allowedChildren[0]) }}
                className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title={`${LEVEL_LABELS[allowedChildren[0]]} 추가`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
                className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title="하위 항목 추가"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
            {menuOpen && (
              <div className="absolute right-0 top-7 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px]">
                {allowedChildren.map((childType) => {
                  const ChildIcon = LEVEL_ICONS[childType] || FolderOpen
                  const childColor = LEVEL_COLORS[childType] || 'text-slate-600 bg-slate-50'
                  return (
                    <button
                      key={childType}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMenuOpen(false)
                        onAddChild(node.id, node.title, childType)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${childColor}`}>
                        <ChildIcon className="w-3 h-3" />
                      </div>
                      {LEVEL_LABELS[childType]} 추가
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {level > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRemoveChild(0, node.id)
            }}
            className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
            title="제거"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child: any, idx: number) => (
            <TreeNodeView
              key={child.id}
              node={child}
              level={level + 1}
              index={idx}
              siblingCount={node.children.length}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onRemoveChild={(parentId, childId) => onRemoveChild(parentId === 0 ? node.id : parentId, childId)}
              onMoveChild={(parentId, childId, dir) => onMoveChild(parentId === 0 ? node.id : parentId, childId, dir)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function findInHierarchy(node: any, id: number): any | null {
  if (!node) return null
  if (node.id === id) return node
  if (node.children) {
    for (const child of node.children) {
      const found = findInHierarchy(child, id)
      if (found) return found
    }
  }
  return null
}

function clampTreePanelWidth(width: number, maxWidth = MAX_TREE_PANEL_WIDTH): number {
  return Math.min(Math.max(Math.round(width), MIN_TREE_PANEL_WIDTH), maxWidth)
}

/** Return a new tree where the node matching parentId has its children replaced. */
function replaceChildrenInTree(node: any, parentId: number, newChildren: any[]): any {
  if (!node) return node
  if (node.id === parentId) {
    return { ...node, children: newChildren }
  }
  if (node.children) {
    return {
      ...node,
      children: node.children.map((c: any) => replaceChildrenInTree(c, parentId, newChildren)),
    }
  }
  return node
}

function findParentOf(node: any, targetId: number): any | null {
  if (!node || !node.children) return null
  for (const child of node.children) {
    if (child.id === targetId) return node
    const found = findParentOf(child, targetId)
    if (found) return found
  }
  return null
}
